import React from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, BookOpen, ShieldCheck, Zap } from "lucide-react";

const InsightsPage = () => {
  const articles = [
    {
      title: "Global Psyllium Demand 2026: The Shift to 99% Purity",
      excerpt: "Why pharmaceutical and food-grade importers are moving towards ultra-high purity husk, and the impact on Indian export pricing.",
      category: "Market Report",
      source: "Future Market Insights & Persistence Market Research",
      url: "https://www.futuremarketinsights.com/reports/psyllium-husk-powder-market"
    },
    {
      title: "Superseeds Outlook: India's Emergence in Chia & Flax",
      excerpt: "A deep dive into how climate-smart farming in Rajasthan is positioning India as a competitive alternative to South American chia sources.",
      category: "Harvest Report",
      source: "Mordor Intelligence & Coherent Market Insights",
      url: "https://www.mordorintelligence.com/industry-reports/indian-seed-sector-analysis"
    },
    {
      title: "Adaptogens & Wellness: The Ashwagandha Growth Story",
      excerpt: "Analyzing the 9.7% CAGR in botanical extracts and the rising demand for HPLC-standardized Ayurvedic roots in global supplements.",
      category: "Industry Analysis",
      source: "Grand View Research & TechSci Research",
      url: "https://www.grandviewresearch.com/industry-analysis/ashwagandha-supplements-market-report"
    },
    {
      title: "Understanding Psyllium Husk Grades: 95%, 98%, and 99% Purity",
      excerpt: "A technical guide for pharmaceutical and food manufacturers on selecting the right purity grade for their specific application.",
      category: "Technical Guide",
      url: "https://www.ijcmas.com/9-12-2020/S.%20D.%20Katke,%20et%20al.pdf"
    }
  ];

  const sources = [
    { name: "FMI: Psyllium Report 2026", url: "https://www.futuremarketinsights.com/reports/psyllium-husk-powder-market" },
    { name: "Mordor: India Seed Analysis", url: "https://www.mordorintelligence.com/industry-reports/indian-seed-sector-analysis" },
    { name: "GVR: Ashwagandha 2033 Report", url: "https://www.grandviewresearch.com/industry-analysis/ashwagandha-supplements-market-report" },
    { name: "CMI: Chia Seeds Outlook", url: "https://www.coherentmarketinsights.com/market-insight/chia-seeds-market-1248" },
    { name: "IJCMAS: Psyllium Technical Study", url: "https://www.ijcmas.com/9-12-2020/S.%20D.%20Katke,%20et%20al.pdf" },
    { name: "PMR: Psyllium Industry Data", url: "https://www.persistencemarketresearch.com/market-analysis/psyllium-market" }
  ];

  return (
    <main data-testid="insights-page" className="bg-cream">
      <Helmet>
        <title>Market Insights & 2026 Harvest Reports — Gralon Global</title>
        <meta name="description" content="Expert insights into the Indian botanical export market. 2026 market reports on psyllium husk, chia seeds, and Ashwagandha for global importers." />
      </Helmet>

      {/* Hero */}
      <section className="border-b border-line">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-20">
          <div className="label-eyebrow">Market Intelligence</div>
          <h1 className="font-serif font-light mt-6 text-5xl lg:text-7xl leading-[1.02] tracking-tight text-ink max-w-4xl">
            Data-driven reports,<br />
            <span className="italic text-forest">sourced from the source.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-ink/75 text-lg leading-relaxed">
            Our trade and quality teams synthesize data from global intelligence agencies 
            and local mandi arrivals to provide the most accurate harvest forecasts.
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {articles.map((article, i) => (
            <div key={i} className="group border-t border-line pt-8 flex flex-col h-full">
              <div className="text-[10px] tracking-[0.25em] uppercase text-gold mb-4">
                {article.category}
              </div>
              <h2 className="font-serif text-xl lg:text-2xl text-ink group-hover:text-forest transition-colors duration-300 min-h-[3.5rem]">
                {article.title}
              </h2>
              <p className="mt-4 text-muted text-sm leading-relaxed flex-grow">
                {article.excerpt}
              </p>
              {article.source && (
                <div className="mt-4 text-[10px] text-ink/40 italic">
                  Data sourced from: {article.source}
                </div>
              )}
              <a 
                href={article.url}
                target={article.url.startsWith("http") ? "_blank" : "_self"}
                rel={article.url.startsWith("http") ? "noopener noreferrer" : ""}
                className="mt-6 flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-forest-dark font-medium hover:text-gold transition-colors"
              >
                View Report <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Data Sources Section */}
      <section className="py-20 border-t border-line bg-cream-alt">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row justify-between items-start gap-12">
            <div className="max-w-md">
              <h2 className="font-serif text-3xl text-ink">Global Data Citations</h2>
              <p className="mt-4 text-ink/60 text-sm leading-relaxed">
                We believe in full transparency. Our market insights are built upon 
                consolidated data from the world's leading agricultural and market 
                intelligence platforms.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-6 w-full lg:w-auto">
              {sources.map((s) => (
                <a 
                  key={s.name} 
                  href={s.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-xs tracking-wider text-ink/40 hover:text-forest transition-colors flex items-center gap-2"
                >
                  <div className="w-1 h-1 bg-gold rounded-full" />
                  {s.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-24 bg-forest-deep text-cream overflow-hidden relative">
        <div className="grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="label-eyebrow text-gold">Technical Expertise</div>
              <h2 className="font-serif font-light mt-6 text-4xl lg:text-5xl leading-tight">
                Deep industry knowledge from the <span className="italic text-gold-light">heart of India.</span>
              </h2>
              <p className="mt-8 text-cream/80 text-lg leading-relaxed">
                As a leading <strong>Indian botanical exporter</strong>, Gralon maintains a 
                repository of technical data on moisture content, mesh sizes, and 
                microbial limits for our core catalog.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              {[
                { Icon: ShieldCheck, t: "Compliance", d: "Deep understanding of USP/BP/EP and FSSAI standards." },
                { Icon: BookOpen, t: "Sourcing Data", d: "Real-time updates on crop arrivals and harvest quality." },
                { Icon: Zap, t: "Logistics Flow", d: "Optimizing container loads and lead times for global ports." },
                { Icon: ShieldCheck, t: "Safety", d: "Rigorous testing for aflatoxins and heavy metals." }
              ].map(({ Icon, t, d }) => (
                <div key={t} className="bg-white/5 p-6 backdrop-blur-sm border border-white/10">
                  <Icon className="w-6 h-6 text-gold" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl mt-4">{t}</h3>
                  <p className="text-xs text-cream/60 mt-2 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default InsightsPage;
